import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Zap, ScanLine, Mic, Gauge, Cloud, CheckCircle2, ArrowRight, ExternalLink, ShieldCheck } from "lucide-react";

const SUBSCRIBE_URL = "https://www.kallitag.fr/tarifs";
const SIGNUP_URL = "https://www.kallitag.fr/inscription";

const BENEFITS = [
  { icon: ScanLine, title: "Capture en 3 secondes", desc: "Carte papier (OCR IA), carte NFC Kallitag ou saisie manuelle — la fiche se remplit toute seule." },
  { icon: Mic, title: "Débrief vocal → IA", desc: "Dictez après le rendez-vous : résumé, points clés (budget, échéance, objections) et actions extraits automatiquement." },
  { icon: Gauge, title: "Probabilité de signature", desc: "Un score IA par prospect + synthèse de compte qui évolue à chaque rencontre pour prioriser vos relances." },
  { icon: Cloud, title: "Sync CRM en 1 clic", desc: "Poussez vos contacts et notes vers HubSpot ou Salesforce, sans ressaisie." },
];

const STEPS = [
  { n: "1", title: "Scannez", desc: "Sur le stand, capturez le prospect en un geste." },
  { n: "2", title: "Débriefez", desc: "Enregistrez votre débrief vocal, l'IA fait le reste." },
  { n: "3", title: "Relancez", desc: "Score, synthèse et relance email prêts — puis sync CRM." },
];

const PLANS = [
  { slug: "solo", name: "Solo", price: "24,90 €", unit: "/mois", sub: "1 utilisateur" },
  { slug: "equipe", name: "Équipe", price: "21,90 €", unit: "/licence/mois", sub: "2 à 9 licences", popular: true },
  { slug: "entreprise", name: "Entreprise", price: "19,90 €", unit: "/licence/mois", sub: "10 licences et +" },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0b1220] text-white" data-testid="landing-page">
      {/* Nav */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg">
            <Zap className="w-5 h-5 text-white" fill="currentColor" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold">Kallitag</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-sky-300/80">Lead Capture</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/produit" data-testid="landing-nav-produit" className="hidden sm:inline-flex text-sm text-slate-300 hover:text-white px-3 py-2">Le produit</Link>
          <Link to="/login" data-testid="landing-nav-login">
            <Button variant="outline" className="rounded-xl bg-transparent border-white/20 text-white hover:bg-white/10">Se connecter</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-sky-300 bg-sky-500/10 rounded-full px-3 py-1">
            <Zap className="w-3.5 h-3.5" /> L'app des commerciaux sur salons
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 leading-[1.05]">
            Ne perdez plus jamais un lead de salon
          </h1>
          <p className="text-base sm:text-lg text-slate-300 mt-5 max-w-xl">
            Capturez, débriefez à la voix et laissez l'IA scorer et synthétiser chaque prospect — puis synchronisez vers votre CRM en un clic.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-hero-cta">
              <Button className="rounded-xl h-12 px-6 text-base font-semibold bg-blue-600 hover:bg-blue-500">
                Créer mon compte · essai 7 j gratuit <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/login" data-testid="landing-hero-login">
              <Button variant="outline" className="rounded-xl h-12 px-6 text-base bg-transparent border-white/20 text-white hover:bg-white/10">
                J'ai déjà un compte
              </Button>
            </Link>
          </div>
          <p className="text-xs text-slate-400 mt-4">Sans carte bancaire · Sans engagement · 7 jours offerts</p>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 bg-blue-500/20 blur-3xl rounded-full" />
          <img src="/ads/ad-scan.jpg" alt="Commercial capturant un prospect sur un salon avec Kallitag Lead Capture"
               className="relative rounded-3xl border border-white/10 shadow-2xl w-full object-cover" data-testid="landing-hero-image" />
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white text-[#0b1220]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-center">Tout ce qu'il faut pour convertir vos salons</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 p-5 hover:shadow-lg transition-shadow" data-testid={`landing-benefit-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-blue-600/10 flex items-center justify-center">
                  <b.icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-heading font-bold mt-4">{b.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-50 text-[#0b1220]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-center">3 étapes, zéro ressaisie</h2>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 p-6 text-center" data-testid={`landing-step-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white font-heading font-extrabold text-lg flex items-center justify-center mx-auto">{s.n}</div>
                <h3 className="font-heading font-bold mt-4">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="bg-white text-[#0b1220]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid sm:grid-cols-3 gap-6 text-center">
          {[["3 s", "pour capturer un prospect"], ["100 %", "de vos leads débriefés"], ["1 clic", "vers HubSpot & Salesforce"]].map((s, i) => (
            <div key={i} data-testid={`landing-stat-${i}`}>
              <div className="font-heading text-4xl font-extrabold text-blue-600">{s[0]}</div>
              <div className="text-sm text-slate-600 mt-1">{s[1]}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-slate-50 text-[#0b1220]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-center">Des offres simples</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {PLANS.map((p) => (
              <a key={p.slug} href={`${SUBSCRIBE_URL}?plan=${p.slug}`} target="_blank" rel="noopener noreferrer"
                 data-testid={`landing-plan-${p.slug}`}
                 className={`block rounded-2xl border p-5 text-center transition-all hover:-translate-y-1 hover:shadow-lg ${p.popular ? "border-blue-600 bg-blue-600/5" : "border-slate-200 bg-white"}`}>
                {p.popular && <span className="text-[10px] font-bold uppercase tracking-wide text-blue-600">Populaire</span>}
                <div className="font-heading font-bold mt-1">{p.name}</div>
                <div className="font-heading text-3xl font-extrabold mt-2">{p.price}</div>
                <div className="text-xs text-slate-500">{p.unit}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.sub}</div>
                <div className="mt-4 text-sm font-semibold text-blue-600 inline-flex items-center gap-1">S'abonner <ArrowRight className="w-4 h-4" /></div>
              </a>
            ))}
          </div>
          <p className="text-center text-sm text-slate-500 mt-6 flex items-center justify-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Essai gratuit 7 jours · sans carte bancaire · tarif dégressif dès 2 licences
          </p>
        </div>
      </section>

      {/* Final CTA + footer */}
      <section className="bg-[#0b1220]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Prêt à transformer vos salons en pipeline ?</h2>
          <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-final-cta">
            <Button className="mt-6 rounded-xl h-12 px-8 text-base font-semibold bg-blue-600 hover:bg-blue-500">
              Créer mon compte gratuit <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
        <footer className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-400">
            <span>© 2026 Kallitag · Lead Capture</span>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-center">
              <Link to="/confidentialite" className="hover:text-white inline-flex items-center gap-1.5" data-testid="landing-privacy-link">
                <ShieldCheck className="w-4 h-4" /> Confidentialité
              </Link>
              <Link to="/mentions-legales" className="hover:text-white" data-testid="landing-legal-link">Mentions légales</Link>
              <Link to="/cgu" className="hover:text-white" data-testid="landing-cgu-link">CGU / CGV</Link>
              <Link to="/dpa" className="hover:text-white" data-testid="landing-dpa-link">DPA</Link>
              <Link to="/login" className="hover:text-white">Se connecter</Link>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
}
